import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  Select,
  Spin,
  Avatar,
  Tooltip,
  Checkbox,
  Tag,
} from "antd";
import CustomButton from "../../../../common/custom-button/custom-button";
import styles from "./student-report.module.css";
import {
  ecdNotifyTeacher,
  getEcdStudents,
  getStudentListReportList,
  notifyTeacherReportCard,
  publishEcdReportCard,
  publishReportData,
} from "../../../../services/reportCard";
import StudentExamDetailsModal from "./components/StudentExamDetailsModal/StudentExamDetailsModal";
import ReportCardModal from "./components/ReportCardModal/ReportCardModal";
import debounce from "lodash.debounce";
import NotifyTeacherModal from "./components/notify-teacher-modal/notify-teacher-modal";
import { toast } from "react-toastify";

const { Option } = Select;

const StudentReportList = ({ 
  form, 
  onClick, 
  exam,
  isECDProgram,
  selectedTerm,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [viewReportModal, setViewReportModal] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [loading, setLoading] = useState(false);
  const [studentData, setStudentData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [examModalVisible, setExamModalVisible] = useState(false);
  const [selectedStudentForModal, setSelectedStudentForModal] = useState(null);
  const [selectedStudentForModalData, setSelectedStudentForModalData] =
    useState({});

  const handleViewDetails = (studentId) => {
    setSelectedStudentForModal(studentId);
    setExamModalVisible(true);
  };

  const getStudentReport = async (sectionId, search = "") => {
    if (!sectionId || (!isECDProgram && !exam?.id) || (isECDProgram && !selectedTerm)) return;
    setLoading(true);
    try {
      const params = {
        classroom_id: sectionId,
        name: search,
      };
      let response;
      if (isECDProgram) {
        params.term_id = selectedTerm;
        response = await getEcdStudents(params);
      } else {
        params.exam_id = exam?.id;
        response = await getStudentListReportList(params);
      }
      const students = response?.data?.data || [];
      const formatted = students.map((student) => ({
        key: student.id,
        name: student.name,
        profile_picture: student.profile_picture,
        class: form?.classroom_name || "N/A",
        teacher: form?.class_teacher || "N/A",
        examName: exam?.name || "N/A",
        examType: exam?.type || "N/A",
        totalExams: student.total_subjects,
        givenExams: student.marked_subjects,
        is_published: student.is_published,
        is_notified: student.is_notified,
      }));
      setStudentData(formatted);
    } catch (error) {
      console.error("Error fetching student report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (form?.sections?.length > 0) {
      const firstSectionId = form.sections[0].section_id;
      setSelectedSectionId(firstSectionId);
      getStudentReport(firstSectionId);
    }
  }, [form, exam]);

  const handleSectionChange = (sectionId) => {
    setSelectedSectionId(sectionId);
    getStudentReport(sectionId, searchValue);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    handleDebounceSearch(selectedSectionId, value);
  };

  const handleDebounceSearch = useCallback(
    debounce((sectionId, value) => {
      if (sectionId) getStudentReport(sectionId, value);
    }, 400),
    []
  );

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectAll(
      studentData
        .filter(
          (item) =>
            item.givenExams === item.totalExams && item.is_published === false
        )
        .every((item) => newSelectedRowKeys.includes(item.key))
    );
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const eligibleKeys = studentData
        .filter(
          (item) =>
            item.givenExams === item.totalExams && item.is_published === false
        )
        .map((item) => item.key);
      setSelectedRowKeys(eligibleKeys);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const showModal = (studentId) => {
    setSelectedStudentId(studentId);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedStudentId("");
  };

  const handleSubmit = async (data) => {
    try {
      const payload = {
        message: data?.comment,
        teacher_id: data?.teacher,
        student_id: selectedStudentId,
      };
      if (isECDProgram) {
        payload.term_id = selectedTerm;
        await ecdNotifyTeacher(payload);
      } else {
        payload.exam_id = exam?.id;
        await notifyTeacherReportCard(payload);
      }
      toast.success("Notification sent successfully.");
      if (selectedSectionId) {
        getStudentReport(selectedSectionId, searchValue);
      }
      return true;
    } catch (error) {
      toast.error("Failed to send notification.");
      return false;
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled:
        record.givenExams !== record.totalExams || record.is_published === true,
    }),
  };

  const selectedSectionName = useMemo(() => {
    return form?.sections?.find((y) => y?.section_id === selectedSectionId);
  }, [form?.sections, selectedSectionId]);

  const columns = [
    {
      title: <div className={styles.headerCell}>Student Name</div>,
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar
            src={record.profile_picture || null}
            alt={record.name}
            style={{ backgroundColor: "#ccc" }}
          >
            {!record.profile_picture && record.name?.charAt(0)}
          </Avatar>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: <div className={styles.headerCell}>Level & Class</div>,
      dataIndex: "class",
      render: () => {
        const level = form?.level_name || "";
        const sections = selectedSectionName?.section_name || "";
        return `${level} - ${sections}`;
      },
    },
    ...(!isECDProgram 
      ? [
          {
            title: <div className={styles.headerCell}>Exam Name</div>,
            dataIndex: "examName",
          },
          {
            title: <div className={styles.headerCell}>Exam Type</div>,
            dataIndex: "examType",
            render: (text) => text?.split("_").join(" ") || "N/A",
          },
        ]
      : []),
    {
      title: (
        <div className={styles.headerCell}>
          {isECDProgram ? 'Comment Assigned' : 'Marks Allocation'}
        </div>
      ),
      sorter: (a, b) =>
        a.givenExams / a.totalExams - b.givenExams / b.totalExams,
      render: (_, record) => `${record.givenExams}/${record.totalExams}`,
      onCell: (record) => ({
        style: {
          backgroundColor:
            record.givenExams !== record.totalExams ? "#FCEEED" : undefined,
        },
      }),
    },
    {
      title: <div className={styles.headerCell}>Publish Status</div>,
      render: (_, record) => {
        const published = record?.is_published;

        return (
          <Tag
            className={styles.statusTag}
            color={published ? "success" : "warning"}
          >
            {published ? "Published" : "Pending"}
          </Tag>
        );
      },
    },
    {
      title: <div className={styles.headerCell}>Raise Concern</div>,
      render: (_, record) => (
        <CustomButton
          title="Notify"
          onClick={() => showModal(record.key)}
          disabled={
            record?.is_notified || record?.totalExams === record?.givenExams
          }
        />
      ),
    },
    {
      title: <div className={styles.headerCell}>Exam Attended</div>,
      render: (_, record) => (
        <CustomButton
          title="View Details"
          onClick={() => {
            handleViewDetails(record.key);
            setSelectedStudentForModalData(record);
          }}
        />
      ),
    },
  ];

  const publishData = async () => {
    if (
      (examModalVisible && !selectedStudentForModalData?.key) ||
      (!examModalVisible && selectedRowKeys.length === 0)
    ) {
      return;
    }

    try {
      const payload = {
        student_ids: examModalVisible
          ? [selectedStudentForModal]
          : selectedRowKeys,
      };
      if (isECDProgram) {
        payload.term_id = selectedTerm;
        await publishEcdReportCard(payload);
      } else {
        payload.exam_id = exam?.id;
        await publishReportData(payload);
      }
      toast.success("Reports published successfully.");
      setSelectedRowKeys([]);
      setSelectAll(false);
      getStudentReport(selectedSectionId, searchValue);
      setExamModalVisible(false);
    } catch (error) {
      console.error("Publish error:", error);
      toast.error("Failed to publish reports.");
    }
  };

  const allCheckboxDisabled = studentData?.every(
    (record) => record.givenExams !== record.totalExams || record.is_published === true
  );

  return (
    <>
      <div className={styles.topControls}>
        <span className={styles.backLink} onClick={onClick}>
          &lt; List of Forms
        </span>

        <div className={styles.filterRow}>
          <Input.Search
            placeholder="Search by student"
            className={styles.searchInput}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
          />

          <Select
            value={selectedSectionId}
            onChange={handleSectionChange}
            className={styles.select}
            placeholder="Select Section"
          >
            {form?.sections?.map((section) => (
              <Option key={section.section_id} value={section.section_id}>
                {section.section_name}
              </Option>
            ))}
          </Select>
        </div>

        <div className={styles.selectAllContainer}>
          <Checkbox
            checked={selectAll}
            onChange={handleSelectAll}
            disabled={allCheckboxDisabled}
            style={{ color: "#22c55e" }}
          >
            Select All Students
          </Checkbox>
        </div>
      </div>

      <Spin spinning={loading}>
        <div className={styles.tableWrapper}>
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={studentData}
            pagination={false}
            className={styles.table}
            bordered={false}
            scroll={{ x: "max-content" }}
          />
        </div>
      </Spin>

      <div className={styles.publishBtnWrapper}>
        <Tooltip
          title={
            selectedRowKeys.length === 0 ? "Select at least one student" : ""
          }
          placement="top"
          open={selectedRowKeys.length === 0 ? undefined : false}
          overlayStyle={{ maxWidth: 200 }}
        >
          <span style={{ display: "inline-block" }}>
            <CustomButton
              title="Publish Report"
              onClick={publishData}
              disabled={selectedRowKeys.length === 0}
            />
          </span>
        </Tooltip>
      </div>

      <NotifyTeacherModal
        visible={isModalVisible}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      />
      <StudentExamDetailsModal
        visible={examModalVisible}
        studentId={selectedStudentForModal}
        examId={exam?.id}
        onClose={() => setExamModalVisible(false)}
        publishData={publishData}
        selectedStudentForModalData={selectedStudentForModalData}
        onClickViewReport={() => {
          setExamModalVisible(false);
          setViewReportModal(true);
        }}
        isECDProgram={isECDProgram}
        selectedTerm={selectedTerm}
      />
      <ReportCardModal
        visible={viewReportModal}
        onClose={() => setViewReportModal(false)}
        studentId={selectedStudentForModal}
        examDetails={exam}
        handleBack={() => {
          setViewReportModal(false);
          setExamModalVisible(true);
        }}
        isECDProgram={isECDProgram}
        selectedTerm={selectedTerm}
      />
    </>
  );
};

export default StudentReportList;
